import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Modal,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useInitiativesQuery } from '../../hooks/queries';
import { OrganizationFilterDropdown, useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Circle, G } from 'react-native-svg';
import { PlusIcon, ExportIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, EllipsisVerticalIcon, EditIcon, TrashIcon, XIcon } from '../../components/Icons';
import { initiativesApi, goalsApi, kpisApi, risksApi } from '../../services/apiService';
import { exportInitiativesToExcel } from '../../utils/excelExport';
import { InitiativeList, StrategicGoalList } from '../../types';
import { COLORS, FONTS, LABELS } from '../../constants/config';
import { InitiativesSkeleton, EmptyState, PageContainer, Navbar, SKELETON_DIMENSIONS, ViewOnlyBadge, Breadcrumb } from '../../components';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';

type RouteProps = RouteProp<MainStackParamList, 'Initiatives'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// Circular Gauge Component
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  textColor?: string;
  trackColor?: string;
}

const CircularGauge = ({ percentage, size = 80, strokeWidth = 6, textColor = '#414042', trackColor = '#E5E7EB' }: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Color based on achievement percentage
  // >= 100%: Green (ممتاز), >= 80%: Orange (جيد), < 80%: Red (يحتاج تحسين)
  const getColor = () => {
    if (percentage >= 100) return '#22C55E'; // Green - Excellent
    if (percentage >= 80) return '#F97316'; // Orange - Good
    return '#EF4444'; // Red - Needs Improvement
  };

  // Calculate marker dot position (at the end of the progress arc)
  const progressAngle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const markerX = center + radius * Math.cos(progressAngle);
  const markerY = center + radius * Math.sin(progressAngle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={getColor()}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Marker dot at progress end */}
        <G>
          <Circle
            cx={markerX}
            cy={markerY}
            r={6}
            fill={getColor()}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        </G>
      </Svg>
      {/* Percentage text */}
      <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ fontSize: 16, fontFamily: FONTS.numbers, fontWeight: '500', color: textColor }}>
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Initiative Card Component
// ============================================================

interface ThemedCardStyles {
  card: object;
  cardTitle: object;
  cardValue: object;
  cardDescription: object;
  sectionTitle: object;
  pageTitle: object;
  pageSubtitle: object;
}

interface InitiativeCardProps {
  initiative: InitiativeList;
  onPress: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isViewOnly?: boolean;
  canDelete?: boolean;
  themedCardStyles?: ThemedCardStyles;
  iconColor?: string;
  textColor?: string;
  trackColor?: string;
  dropdownBg?: string;
  dropdownBorder?: string;
  statBoxBg?: string;
  t: (key: string) => string;
  translate: (text: string | undefined | null) => string;
  translateOrg: (name: string | undefined | null) => string;
}

const InitiativeCard = ({ initiative, onPress, onEdit, onDelete, isViewOnly, canDelete = true, themedCardStyles, iconColor = '#414042', textColor = '#414042', trackColor = '#E5E7EB', dropdownBg = '#FFFFFF', dropdownBorder = '#E2E8F0', statBoxBg = '#F1F5F9', t, translate, translateOrg }: InitiativeCardProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const isPending = initiative.approval_status === 'pending';
  // Use real achievement percentage from API (default to 0 if not available)
  const percentage = initiative.achievement_percentage ?? 0;
  const organizationName = translateOrg(initiative.department_name) || t('common.notSpecified');

  const handleMenuPress = () => {
    setShowMenu(!showMenu);
  };

  const handleEdit = () => {
    setShowMenu(false);
    onEdit();
  };

  const handleDelete = () => {
    setShowMenu(false);
    onDelete();
  };

  return (
    <View style={[styles.initiativeCard, { minHeight: SKELETON_DIMENSIONS.INITIATIVE_CARD_HEIGHT, flex: 1 }, themedCardStyles?.card]}>
      {/* Top Section: Different layout for pending vs approved */}
      <View style={styles.cardTopSection}>
        {isPending ? (
          // Pending State: Purple badge on left, title section on right
          <>
            {/* Left: Status Badge (Purple) */}
            <View style={styles.pendingBadge}>
              <Text style={styles.pendingBadgeText}>{t('initiatives.pendingApproval')}</Text>
            </View>
            {/* Right: Title and Org Badge */}
            <View style={styles.cardTitleSection}>
              <Text style={[styles.cardTitle, themedCardStyles?.cardTitle]} numberOfLines={2}>
                {translate(initiative.title)}
              </Text>
              <View style={styles.orgBadge}>
                <Text style={styles.orgBadgeText}>{organizationName}</Text>
              </View>
            </View>
          </>
        ) : (
          // Approved/In Progress State: Gauge on left, title section on right
          <>
            {/* Left: Circular Gauge */}
            <View style={styles.gaugeContainer}>
              <CircularGauge percentage={percentage} size={80} strokeWidth={6} textColor={textColor} trackColor={trackColor} />
            </View>
            {/* Right: Title and Org Badge */}
            <View style={styles.cardTitleSection}>
              <Text style={[styles.cardTitle, themedCardStyles?.cardTitle]} numberOfLines={2}>
                {translate(initiative.title)}
              </Text>
              <View style={styles.orgBadge}>
                <Text style={styles.orgBadgeText}>{organizationName}</Text>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Description */}
      <Text style={[styles.cardDescription, themedCardStyles?.cardDescription]} numberOfLines={2}>
        {translate(initiative.description) || '—'}
      </Text>

      {/* Created by */}
      {initiative.created_by_details?.full_name ? (
        <Text style={[styles.cardCreatedBy, themedCardStyles?.cardDescription]} numberOfLines={1}>
          {t('initiatives.createdBy')}: {initiative.created_by_details.full_name}
        </Text>
      ) : null}

      {/* Pending Deletion Badge */}
      {initiative.has_pending_deletion && <PendingDeletionBadge compact />}

      {/* Spacer to push stats and footer to bottom */}
      <View style={{ flex: 1 }} />

      {/* Stats Row - Gray boxes */}
      <View style={styles.statsRow}>
        <View style={[styles.statBox, { backgroundColor: statBoxBg }]}>
          <Text style={[styles.statLabel, themedCardStyles?.cardDescription]}>{t('initiatives.kpiCount')}</Text>
          <Text style={[styles.statValue, themedCardStyles?.cardValue]}>{initiative.kpi_count ?? 0}</Text>
        </View>
        <View style={[styles.statBox, { backgroundColor: statBoxBg }]}>
          <Text style={[styles.statLabel, themedCardStyles?.cardDescription]}>{t('initiatives.activityCount')}</Text>
          <Text style={[styles.statValue, themedCardStyles?.cardValue]}>{initiative.activity_count ?? 0}</Text>
        </View>
      </View>

      {/* Footer: View Details (Left) + Menu (Right) */}
      <View style={styles.cardFooter}>
        <TouchableOpacity style={styles.viewDetailsButton} onPress={onPress}>
          <ChevronLeftIcon size={14} color={iconColor} />
          <Text style={[styles.viewDetailsText, { color: iconColor }]}>{t('common.viewDetails')}</Text>
        </TouchableOpacity>
        {/* Menu - hidden for view-only users (executive) */}
        {!isViewOnly && (
          <View style={styles.menuContainer}>
            <TouchableOpacity style={styles.menuButton} onPress={handleMenuPress}>
              <EllipsisVerticalIcon size={20} color={iconColor} />
            </TouchableOpacity>
            {showMenu && (
              <>
                <Pressable style={styles.menuOverlay} onPress={() => setShowMenu(false)} />
                <View style={[styles.cardDropdownMenu, { backgroundColor: dropdownBg, borderColor: dropdownBorder }]}>
                  <TouchableOpacity style={[styles.cardDropdownItem, { borderBottomColor: dropdownBorder }]} onPress={handleEdit}>
                    <Text style={[styles.cardDropdownItemText, { color: iconColor }]}>{t('initiatives.editInitiative')}</Text>
                    <EditIcon size={16} color={iconColor} />
                  </TouchableOpacity>
                  {canDelete && (
                    <TouchableOpacity style={styles.cardDropdownItemDelete} onPress={handleDelete}>
                      <Text style={styles.cardDropdownItemTextDelete}>{t('common.delete')}</Text>
                      <TrashIcon size={16} color="#EF4444" />
                    </TouchableOpacity>
                  )}
                </View>
              </>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

// ============================================================
// Pagination Component
// ============================================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange, nextLabel, previousLabel }: PaginationProps & { nextLabel: string; previousLabel: string }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
      {/* Previous: السابق > */}
      <TouchableOpacity
        style={[styles.paginationNavButton, isFirstPage && styles.paginationNavButtonDisabled]}
        onPress={() => onPageChange(Math.max(currentPage - 1, 1))}
        disabled={isFirstPage}
      >
        <Text style={styles.paginationNavText}>{previousLabel}</Text>
        <ChevronRightIcon size={13} color="#334155" />
      </TouchableOpacity>

      {/* Page Numbers */}
      <View style={[styles.paginationPages, { flexDirection: rtlRow }]}>
        {pages.map((page) => (
          <TouchableOpacity
            key={page}
            style={[
              styles.paginationPage,
              currentPage === page && styles.paginationPageActive,
            ]}
            onPress={() => onPageChange(page)}
          >
            <Text
              style={[
                styles.paginationPageText,
                currentPage === page && styles.paginationPageTextActive,
              ]}
            >
              {page}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Next: < التالي */}
      <TouchableOpacity
        style={[styles.paginationNavButton, isLastPage && styles.paginationNavButtonDisabled]}
        onPress={() => onPageChange(Math.min(currentPage + 1, totalPages))}
        disabled={isLastPage}
      >
        <ChevronLeftIcon size={13} color="#334155" />
        <Text style={styles.paginationNavText}>{nextLabel}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Main Screen
// ============================================================

export default function InitiativesScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { goalId, organizationId } = route.params || {};
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme-aware styles for cards
  const themedCardStyles: ThemedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
    sectionTitle: { color: colors.textPrimary },
    pageTitle: { color: colors.textPrimary },
    pageSubtitle: { color: colors.textMuted },
  };

  // Theme colors for specific elements
  const themeColors = {
    statBox: isDark ? '#374151' : '#F1F5F9',
    filterDropdown: colors.card,
    filterDropdownBorder: colors.cardBorder,
    filterDropdownText: colors.textPrimary,
    applyFilterBg: isDark ? '#374151' : '#F1F5F9',
    applyFilterText: colors.textPrimary,
    exportButton: colors.card,
    exportButtonBorder: colors.cardBorder,
    exportButtonText: colors.textPrimary,
  };

  // Responsive breakpoints (for backward compatibility)
  const isSmallScreen = isMobile;

  // Check if user is org_employee (limited permissions)
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  // Check if user is strategy_team (they can't add initiatives)
  const isStrategyTeam = (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false) || (user?.is_superuser ?? false);
  // DTS 2.2 row "اقتراح العناصر التشغيلية" — only رئيس القسم (and
  // superuser) creates operational items. Employee column = —;
  // فريق الاستراتيجية approves but does NOT create. Gate the Add
  // button on the actual perm instead of the coarse !isViewOnly
  // check, which was wrongly showing Add to plain Employees when
  // data entry was open.
  const canCreateOperationalItem = (
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );
  // DTS 2.2: only Strategy Team (via hrcpms.soft_delete) can delete approved
  // executive items. Section Head can withdraw their own draft proposals
  // before the approval workflow advances — i.e. while approval_status is
  // still 'draft' or 'pending'. Once status flips to 'approved' the delete
  // affordance disappears for them; they'd need Strategy Team to soft-delete.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );
  const canDeleteInitiative = (initiative: InitiativeList): boolean => {
    if (canSoftDelete) return true;
    if (!canCreateOperationalItem) return false;
    return initiative.approval_status !== 'approved';
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 6;

  const queryClient = useQueryClient();

  // Delete confirmation modal state
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [initiativeToDelete, setInitiativeToDelete] = useState<InitiativeList | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filter state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [strategicGoals, setStrategicGoals] = useState<StrategicGoalList[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(goalId || null);
  const [tempSelectedGoalId, setTempSelectedGoalId] = useState<number | null>(goalId || null);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(organizationId ?? null);
  const effectiveOrgId = selectedOrgId ?? organizationId ?? undefined;

  // Use React Query for initiatives
  const {
    data: initiativesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInitiativesQuery({
    page: currentPage,
    pageSize: pageSize,
    goal: selectedGoalId || goalId,
    organization: effectiveOrgId,
    // Executive (view-only) users should only see approved initiatives
    approval_status: isViewOnly ? 'approved' : undefined,
    year: selectedYear,
  });

  const initiatives = initiativesData?.results || [];

  // Auto-refresh when screen regains focus (e.g. after add/edit)
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Update total pages when data changes
  useEffect(() => {
    if (initiativesData?.count) {
      setTotalPages(Math.ceil(initiativesData.count / pageSize));
    }
  }, [initiativesData?.count, pageSize]);

  // Fetch strategic goals for filter dropdown
  useEffect(() => {
    const fetchStrategicGoals = async () => {
      setLoadingGoals(true);
      try {
        const data = await goalsApi.list({ page_size: 100 });
        setStrategicGoals(data.results || []);
      } catch (err) {
        console.error('Error fetching strategic goals:', err);
      } finally {
        setLoadingGoals(false);
      }
    };
    fetchStrategicGoals();
  }, []);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGoalId, selectedOrgId]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handleViewDetails = (id: number) => {
    navigation.navigate('InitiativeDetail', { id });
  };

  const handleEditInitiative = (initiative: InitiativeList) => {
    navigation.navigate('AddInitiative', { initiativeId: initiative.id });
  };

  const handleDeletePress = (initiative: InitiativeList) => {
    setInitiativeToDelete(initiative);
    setShowDeletionImpact(true);
  };

  const handleConfirmDelete = async () => {
    if (!initiativeToDelete) return;

    setDeleting(true);
    try {
      const response = await initiativesApi.delete(initiativeToDelete.id);

      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t('initiatives.deleteSuccess'));
      }

      // Invalidate the query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
    } catch (err: any) {
      if (err.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      } else if (err.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(err.response?.data?.detail || t('messages.error.deleteFailed'));
      }
    } finally {
      setDeleting(false);
      setShowDeletionImpact(false);
      setInitiativeToDelete(null);
    }
  };

  // Filter handlers
  const handleToggleFilterDropdown = () => {
    setShowFilterDropdown(!showFilterDropdown);
    if (!showFilterDropdown) {
      setTempSelectedGoalId(selectedGoalId);
    }
  };

  const handleSelectGoal = (id: number | null) => {
    setTempSelectedGoalId(id);
    setSelectedGoalId(id); // Auto-apply filter on selection
    setShowFilterDropdown(false);
  };

  const handleApplyFilter = () => {
    setSelectedGoalId(tempSelectedGoalId);
    setShowFilterDropdown(false);
  };

  const handleClearFilter = () => {
    setSelectedGoalId(null);
    setTempSelectedGoalId(null);
  };

  // Get selected goal name for display
  const getSelectedGoalName = () => {
    if (!tempSelectedGoalId) return t('filters.allStrategicGoals');
    const goal = strategicGoals.find(g => g.id === tempSelectedGoalId);
    return translate(goal?.title) || t('filters.allStrategicGoals');
  };

  // Handle Excel export - fetch ALL data, not just current page
  const handleExportExcel = async () => {
    if (initiativesData?.count === 0) {
      showError(t('initiatives.noInitiativesToExport'));
      return;
    }

    setIsExporting(true);
    try {
      // Fetch ALL initiatives (not just current page)
      const params: Record<string, any> = { page_size: 9999 };
      if (selectedGoalId || goalId) params.goal = selectedGoalId || goalId;
      if (effectiveOrgId) params.organization = effectiveOrgId;
      if (isViewOnly) params.approval_status = 'approved';

      const allInitiativesData = await initiativesApi.list(params);
      const allInitiatives = allInitiativesData?.results || [];

      if (allInitiatives.length === 0) {
        showError(t('initiatives.noInitiativesToExport'));
        return;
      }

      // Build initiative ID → title mapping
      const initiativeTitleMap: Record<number, string> = {};
      allInitiatives.forEach((init: any) => {
        initiativeTitleMap[init.id] = init.title;
      });

      // Fetch KPIs for all these initiatives
      const initiativeIds = allInitiatives.map((init: any) => init.id);
      const kpiPromises = initiativeIds.map((id: number) =>
        kpisApi.list({ initiative: id, page_size: 9999 })
      );
      const kpiResults = await Promise.all(kpiPromises);
      const allKpis = kpiResults.flatMap((res: any, idx: number) =>
        (res?.results || []).map((kpi: any) => ({
          ...kpi,
          initiative_id: initiativeIds[idx],
          initiative_title: initiativeTitleMap[initiativeIds[idx]] || '',
        }))
      );

      // Fetch risks linked to initiatives (related_type='initiative')
      const risksResp = await risksApi.list({ related_type: 'initiative', page_size: 9999 });
      const allRisks = risksResp?.results || risksResp || [];

      exportInitiativesToExcel(allInitiatives, allKpis, allRisks);
      showSuccess(t('messages.success.exportedInitiatives', { count: allInitiatives.length }));
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showError(t('messages.error.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading && initiatives.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <InitiativesSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={LABELS.error}
        message={error.message}
        actionLabel={LABELS.refresh}
        onAction={refetch}
      />
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.strategicInitiatives') },
          ]}
        />

        {/* Title Section Card - Contains title, button, and filters - Fixed height to match skeleton */}
        <View style={[styles.titleSectionCard, themedCardStyles.card, { minHeight: SKELETON_DIMENSIONS.PAGE_TITLE_HEIGHT }]}>
          {/* Row 1: Title + Add Button */}
          <View style={styles.headerRow}>
            {/* Left side buttons */}
            <View style={styles.headerButtons}>
              {/* Add Operational Element — DTS 2.2 row "اقتراح العناصر
                  التشغيلية" gives this to رئيس القسم (executive_items.
                  create permission). Strategy Team approves but doesn't
                  propose, so the perm cleanly excludes them too. */}
              {canCreateOperationalItem && (
                <TouchableOpacity
                  style={[styles.addButton, { minHeight: 44 }]}
                  onPress={() => navigation.navigate('AddInitiative', {})}
                >
                  <Text style={styles.addButtonText}>{isMobile ? t('common.add') : t('initiatives.addInitiative')}</Text>
                  <PlusIcon size={13} color="#FFFFFF" />
                </TouchableOpacity>
              )}

              {/* Export Button */}
              <TouchableOpacity
                style={[styles.exportButton, { minHeight: 44, backgroundColor: themeColors.exportButton, borderColor: themeColors.exportButtonBorder }]}
                onPress={handleExportExcel}
              >
                <Text style={[styles.exportButtonText, { color: themeColors.exportButtonText }]}>{isMobile ? t('common.export') : t('common.exportExcel')}</Text>
                <ExportIcon size={16} color={colors.textSecondary} />
              </TouchableOpacity>

            </View>

            {/* Title - Right side */}
            <View style={styles.titleContainer}>
              <Text style={[styles.pageTitle, { color: colors.textPrimary }]}>{t('navigation.strategicInitiatives')}</Text>
            </View>
          </View>

          {/* Row 2: Filter Section */}
          <View style={[
            styles.filterRow,
            isMobile && { flexDirection: 'column-reverse', gap: 10, alignItems: 'stretch' }
          ]}>
            {/* Clear Filter Button - Only shown when filter is active (leftmost) */}
            {selectedGoalId && (
              <TouchableOpacity
                style={[styles.clearFilterButton, { minHeight: 44, width: isMobile ? '100%' : 'auto' }]}
                onPress={handleClearFilter}
              >
                <XIcon size={14} color="#EF4444" />
                <Text style={styles.clearFilterText}>{t('filters.clearFilter')}</Text>
              </TouchableOpacity>
            )}

            {/* Apply Filter Button - Left */}
            <TouchableOpacity
              style={[styles.applyFilterButton, { minHeight: 44, width: isMobile ? '100%' : 'auto', backgroundColor: themeColors.applyFilterBg }]}
              onPress={handleApplyFilter}
            >
              <Text style={[styles.applyFilterText, { color: themeColors.applyFilterText }]}>{t('common.applyFilter')}</Text>
            </TouchableOpacity>

            {/* Dropdown - Right (shown last in row = right in RTL) */}
            <View style={[styles.filterDropdownContainer, { width: isMobile ? '100%' : 'auto' }]}>
              <TouchableOpacity
                style={[
                  styles.filterDropdown,
                  {
                    minHeight: 44,
                    backgroundColor: themeColors.filterDropdown,
                    borderColor: themeColors.filterDropdownBorder,
                    width: '100%',
                  }
                ]}
                onPress={handleToggleFilterDropdown}
              >
                <ChevronDownIcon size={16} color={colors.textSecondary} />
                <Text
                  style={[styles.filterDropdownText, { color: themeColors.filterDropdownText }]}
                  numberOfLines={1}
                >
                  {getSelectedGoalName()}
                </Text>
              </TouchableOpacity>

              {/* Dropdown Menu */}
              {showFilterDropdown && (
                <>
                  <Pressable style={styles.filterOverlay} onPress={() => setShowFilterDropdown(false)} />
                  <View style={[styles.filterDropdownMenu, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
                    <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled>
                      {/* All Goals Option */}
                      <TouchableOpacity
                        style={[
                          styles.filterDropdownOption,
                          tempSelectedGoalId === null && styles.filterDropdownOptionSelected,
                          { borderBottomColor: colors.border }
                        ]}
                        onPress={() => handleSelectGoal(null)}
                      >
                        <Text style={[
                          styles.filterDropdownOptionText,
                          { color: colors.textPrimary },
                          tempSelectedGoalId === null && { fontFamily: FONTS.medium }
                        ]}>
                          {t('filters.allStrategicGoals')}
                        </Text>
                      </TouchableOpacity>

                      {/* Loading state */}
                      {loadingGoals ? (
                        <View style={styles.filterLoadingContainer}>
                          <ActivityIndicator size="small" color={colors.primary} />
                        </View>
                      ) : (
                        /* Goals List */
                        strategicGoals.map((goal) => (
                          <TouchableOpacity
                            key={goal.id}
                            style={[
                              styles.filterDropdownOption,
                              tempSelectedGoalId === goal.id && styles.filterDropdownOptionSelected,
                              { borderBottomColor: colors.border }
                            ]}
                            onPress={() => handleSelectGoal(goal.id)}
                          >
                            <Text
                              style={[
                                styles.filterDropdownOptionText,
                                { color: colors.textPrimary },
                                tempSelectedGoalId === goal.id && { fontFamily: FONTS.medium }
                              ]}
                              numberOfLines={2}
                            >
                              {translate(goal.title)}
                            </Text>
                          </TouchableOpacity>
                        ))
                      )}
                    </ScrollView>
                  </View>
                </>
              )}
            </View>

            {/* Organization Filter - rightmost in RTL = first reading position */}
            {canViewAllOrgs && (
              <View style={{ width: isMobile ? '100%' : 'auto', zIndex: 200 }}>
                <OrganizationFilterDropdown
                  selectedOrgId={selectedOrgId}
                  onChange={setSelectedOrgId}
                />
              </View>
            )}
          </View>
        </View>

        {/* Initiatives Grid - minHeight to prevent CLS */}
        <View style={{ minHeight: 600 }}>
          {initiatives.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>{t('initiatives.noInitiatives')}</Text>
            </View>
          ) : (
            <>
              <View style={[styles.initiativesGrid, isSmallScreen && styles.initiativesGridSmall]}>
                {initiatives.map((initiative) => (
                  <View key={initiative.id} style={isSmallScreen ? styles.initiativeCardWrapperSmall : styles.initiativeCardWrapper}>
                    <InitiativeCard
                      initiative={initiative}
                      onPress={() => handleViewDetails(initiative.id)}
                      onEdit={() => handleEditInitiative(initiative)}
                      onDelete={() => handleDeletePress(initiative)}
                      isViewOnly={!canCreateOperationalItem}
                      canDelete={canDeleteInitiative(initiative)}
                      themedCardStyles={themedCardStyles}
                      iconColor={colors.iconDefault}
                      textColor={colors.textPrimary}
                      trackColor={colors.border}
                      dropdownBg={colors.card}
                      dropdownBorder={colors.cardBorder}
                      statBoxBg={themeColors.statBox}
                      t={t}
                      translate={translate}
                      translateOrg={translateOrg}
                    />
                  </View>
                ))}
              </View>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  nextLabel={t('pagination.next')}
                  previousLabel={t('pagination.previous')}
                />
              )}
            </>
          )}
        </View>
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setInitiativeToDelete(null); }}
        onConfirm={handleConfirmDelete}
        entityName={initiativeToDelete?.title || ''}
        fetchImpact={async () => {
          const response = await initiativesApi.deletionImpact(initiativeToDelete!.id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={deleting}
      />
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  contentSmall: {
    padding: 12,
  },
  // Breadcrumb
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Title Section Card - White card containing title, button and filters
  titleSectionCard: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 20,
    gap: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    zIndex: 1000,
    overflow: 'visible',
  },
  // Row 1: Title + Add Button
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  orgFilterRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginTop: 12,
    zIndex: 1100,
  },
  titleContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    fontWeight: '700',
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#A9211F',
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    minHeight: 32,
    gap: 6,
  },
  addButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#f1f5f9',
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    minHeight: 32,
    gap: 6,
  },
  exportButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Row 2: Filter Section
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 20,
    zIndex: 1000,
    overflow: 'visible',
  },
  filterRowSmall: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'flex-end',
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    minHeight: 32,
    gap: 8,
  },
  filterDropdownText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  applyFilterButton: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    minHeight: 32,
  },
  applyFilterText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },
  // Filter dropdown container
  filterDropdownContainer: {
    position: 'relative',
    zIndex: 9999,
    minWidth: 200,
  },
  filterOverlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },
  filterDropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    left: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 50,
    zIndex: 9999,
    marginTop: 4,
    overflow: 'hidden',
  },
  filterDropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    gap: 8,
  },
  filterDropdownOptionSelected: {
    backgroundColor: '#F1F5F9',
  },
  filterDropdownOptionText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    flex: 1,
  },
  filterCheckmark: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#22C55E',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterCheckmarkText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  filterLoadingContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FCA5A5',
    backgroundColor: '#FEF2F2',
    minHeight: 32,
    gap: 6,
  },
  clearFilterText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
  },
  // Initiatives Grid
  initiativesGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 20,
    overflow: 'visible',
    alignItems: 'stretch',
  },
  initiativesGridSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  // Card Wrappers for responsive width
  initiativeCardWrapper: {
    width: 'calc(50% - 10px)' as any,
    display: 'flex' as any,
  },
  initiativeCardWrapperSmall: {
    width: '100%',
    display: 'flex' as any,
  },
  // Initiative Card
  initiativeCard: {
    width: '100%',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: 16,
    overflow: 'visible',
  },
  cardTopSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
    overflow: 'hidden',
  },
  gaugeContainer: {
    // Gauge on the left
  },
  cardTitleSection: {
    flex: 1,
    alignItems: 'flex-end',
    gap: 12,
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  // Purple badge for pending status - appears at top left
  pendingBadge: {
    backgroundColor: '#9467BD',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 40,
  },
  pendingBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
  },
  // Gold badge for organization unit name
  orgBadge: {
    backgroundColor: '#A6813F',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 40,
  },
  orgBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
  },
  cardDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 14,
  },
  cardCreatedBy: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    marginTop: 6,
  },
  // Stats Row - Gray boxes
  statsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
    overflow: 'hidden',
  },
  statBox: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: 'flex-end',
    gap: 4,
    overflow: 'hidden',
  },
  statLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  statValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
    textAlign: 'center',
  },
  // Card Footer
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'visible',
    zIndex: 10,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    gap: 8,
  },
  viewDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  menuButton: {
    padding: 4,
  },
  // Empty State
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  // Pagination
  paginationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 32,
    gap: 8,
  },
  paginationNavButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    borderRadius: 8,
    gap: 8,
  },
  paginationNavButtonDisabled: {
    opacity: 0.5,
  },
  paginationNavText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: '#334155',
  },
  paginationPages: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paginationPage: {
    width: 34,
    minHeight: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 7.5,
  },
  paginationPageActive: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  paginationPageText: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '700',
    color: '#334155',
  },
  paginationPageTextActive: {
    color: '#414042',
  },
  // Menu Container & Dropdown
  menuContainer: {
    position: 'relative',
    zIndex: 100,
  },
  menuOverlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99,
  },
  cardDropdownMenu: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px -4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    zIndex: 100,
    minWidth: 160,
    marginBottom: 4,
    overflow: 'hidden',
  },
  cardDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  cardDropdownItemDelete: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  cardDropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  cardDropdownItemTextDelete: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
  },
  // Delete Confirmation Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    gap: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    flex: 1,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 22,
  },
  modalInfoBox: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 12,
    gap: 6,
    borderWidth: 1,
    borderColor: '#FCD34D',
  },
  modalInfoTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#92400E',
    textAlign: 'right',
    marginBottom: 4,
  },
  modalInfoText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#92400E',
    textAlign: 'right',
    lineHeight: 20,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonDanger: {
    backgroundColor: '#EF4444',
  },
  modalButtonPrimary: {
    backgroundColor: '#A9211F',
  },
  modalButtonCancel: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  modalButtonTextDanger: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  modalButtonTextCancel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
